// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/MissedNode.js (392 lines).
 * BYTE-IDENTICAL — reactflow → @xyflow/react, AudioPreviewModel → AudioPreviewModal.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Handle, useReactFlow } from '@xyflow/react';
import { Button, Form } from './_rbShim';
import { NodesContext } from '../provider/IvrContext';
import { toast } from 'sonner';
import { PhoneMissed, Settings, Trash2 } from 'lucide-react';
import IvrDrawer from '../Helpers/IvrDrawer';
import AudioPreviewModal from '../Helpers/AudioPreviewModal';
import { triggerHandleConnect, findUpstreamLanguageNode } from '../Helpers/utils';
import {
  BACKEND_API_BASE_URL,
  VOICEMAIL_MAX_DURATION,
  MISSED_NODE_OPTIONS,
  CALLBACK_INTERVAL_OPTIONS,
  CALLBACK_RETRY_COUNT_OPTIONS,
  CALLBACK_DID_MODES,
  CALLBACK_DID_CATEGORIES,
  CALLBACK_ROUTING_ACTIONS,
  DEFAULT_TIME_SCHEDULE,
} from '@/utilities/constant';
import { getApiCall } from '@/service/httpCall';
import {
  API_WEBHOOK_LIST,
  API_SMSWEBHOOK_LIST,
  API_NUMBERS_LIST,
  API_IVR_LIST,
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
} from '@/service/url';
import { useParams } from 'react-router-dom';
import AiAgentCompletionConfig, {
  buildAiCompletionPayload,
  validateAiCompletionRules,
} from '@/features/inbound-calls/AiAgentCompletionConfig';
import { useNodeEnv } from '@/utilities/appEnv';

// Callback is an outgoing call that may also receive calls back, so the DID
// must allow both directions.
const CALLBACK_DID_ROUTING_TYPES = ['both'];

const emptyCallback = () => ({
  callback_delay: '',
  did_mode: CALLBACK_DID_MODES.SAME,
  did_category: '',
  did_id: '',
  routing_action: '',
  routing_target_id: '',
  routing_target_name: '',
  routing_ai_rules: [],
  retry_enabled: false,
  retry_count: '',
  retry_interval: '',
});

const callbackFromAction = (act: any) =>
  act?.type === 'set_callback'
    ? {
        callback_delay: act.callback_delay ?? '',
        did_mode: act.did_mode || CALLBACK_DID_MODES.SAME,
        did_category: act.did_category || '',
        did_id: act.did_id ?? '',
        routing_action: act.routing?.action || '',
        routing_target_id: act.routing?.id ?? '',
        routing_target_name: act.routing?.name || '',
        routing_ai_rules: Array.isArray(act.routing?.completion_type)
          ? act.routing.completion_type
          : [],
        retry_enabled: !!act.retry_enabled,
        retry_count: act.retry_count ?? '',
        retry_interval: act.retry_interval ?? '',
      }
    : emptyCallback();

// Routing is set for the same DID, or for another DID that has none yet.
const callbackNeedsRouting = (cb: any) =>
  cb.did_mode === CALLBACK_DID_MODES.SAME ||
  (cb.did_mode === CALLBACK_DID_MODES.OTHER &&
    cb.did_category === CALLBACK_DID_CATEGORIES.FREE &&
    !!cb.did_id);

const isNumberRouted = (n: any) =>
  (n?.routing_id != null && n.routing_id !== '') || !!n?.routing_destination;

const MissedNode = ({ data, id }: any) => {
  const { updateNodeData, onRemove } = useContext(NodesContext) as any;
  const { getNodes, getEdges } = useReactFlow();
  const { cid } = useParams();
  // Backend NODE_ENV (agent-call-connect node_env). All routing options only on "development";
  // until it loads, or anywhere else, only IVR is offered.
  const isDevelopmentEnv = useNodeEnv() === 'development';

  const mode = data.mode || '';
  const action = data.action || {};
  const maxDuration = data.max_duration || '';

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [promptList, setPromptList] = useState<any[]>([]);
  const [webhookList, setWebhookList] = useState<any[]>([]);
  const [smsWebhookList, setSmsWebhookList] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});

  const [answerLangCodes, setAnswerLangCodes] = useState<any[]>([]);
  const [hasLangParent, setHasLangParent] = useState(false);
  const [draftMode, setDraftMode] = useState(mode);
  const [draftAction, setDraftAction] = useState<any>(action);
  const [draftLanguage, setDraftLanguage] = useState(action.language || '');
  const [draftCategory, setDraftCategory] = useState('general');
  const [draftDuration, setDraftDuration] = useState(maxDuration);
  const [draftWebhookId, setDraftWebhookId] = useState(
    action?.webhook_id || '',
  );
  const [draftSmsWebhookId, setDraftSmsWebhookId] = useState(
    action?.sms_webhook_id || '',
  );
  const [drawerPreview, setDrawerPreview] = useState<any>({
    open: false,
    url: '',
    name: '',
  });
  const [draftCallback, setDraftCallback] = useState<any>(() =>
    callbackFromAction(action),
  );
  const [callbackListsLoaded, setCallbackListsLoaded] = useState(false);
  const [numberList, setNumberList] = useState<any[]>([]);
  const [ivrList, setIvrList] = useState<any[]>([]);
  const [agentList, setAgentList] = useState<any[]>([]);
  const [groupList, setGroupList] = useState<any[]>([]);

  // Callback lists are only needed once the drawer shows the Set Callback form.
  useEffect(() => {
    if (!modalIsOpen || draftMode !== 'set_callback' || callbackListsLoaded)
      return;
    setCallbackListsLoaded(true);
    const asArray = (r: any) => (Array.isArray(r) ? r : []);
    getApiCall(API_NUMBERS_LIST)
      .then((r: any) => setNumberList(asArray(r)))
      .catch(console.log);
    if (cid) {
      getApiCall(API_IVR_LIST.replace(':cid', cid), { fields: 'min' })
        .then((r: any) => setIvrList(asArray(r)))
        .catch(console.log);
    }
    getApiCall(API_AGENTS_LIST, { fields: 'min' })
      .then((r: any) => setAgentList(asArray(r)))
      .catch(console.log);
    getApiCall(API_AGENT_GROUPS_LIST, { fields: 'min' })
      .then((r: any) => setGroupList(asArray(r)))
      .catch(console.log);
  }, [modalIsOpen, draftMode, callbackListsLoaded, cid]);

  const callbackDids = numberList.filter(
    (n: any) =>
      n.number_type === 'DID' &&
      CALLBACK_DID_ROUTING_TYPES.includes(
        String(n.number_routing_type || '').trim().toLowerCase(),
      ),
  );
  const freeDids = callbackDids.filter((n: any) => !isNumberRouted(n));
  // The IVR a DID is routed to (its IVR routing holds the IVR's flow_id; the name is a
  // fallback). null when the DID isn't IVR-routed or the IVR isn't in the list.
  const routedIvrOf = (n: any) => {
    if (String(n?.routing_type || '').trim().toUpperCase() !== 'IVR') return null;
    return (
      ivrList.find((i: any) => String(i.flow_id) === String(n.routing_id)) ||
      (n.routing_destination
        ? ivrList.find((i: any) => String(i.flow_name) === String(n.routing_destination))
        : null) ||
      null
    );
  };
  // routing_id to save for a routed DID: the IVR's own id for IVR routing, else as is.
  const didRoutingTargetId = (n: any) => {
    const ivr = routedIvrOf(n);
    return ivr ? ivr.id : n?.routing_id ?? null;
  };

  // Already-routed DIDs, except those routed to a missed-call IVR. Outside backend NODE_ENV
  // "development" only IVR-routed DIDs are offered (same as the routing actions below).
  const routedDids = callbackDids.filter((n: any) => {
    if (!isNumberRouted(n)) return false;
    if (!isDevelopmentEnv && String(n.routing_type || '').trim().toUpperCase() !== 'IVR') return false;
    return String(routedIvrOf(n)?.ivr_category || '').toLowerCase() !== 'missed';
  });
  const categoryDids =
    draftCallback.did_category === CALLBACK_DID_CATEGORIES.FREE
      ? freeDids
      : draftCallback.did_category === CALLBACK_DID_CATEGORIES.ROUTED
      ? routedDids
      : [];

  const routingTargets =
    draftCallback.routing_action === 'ivr'
      ? ivrList
          .filter(
            (i: any) =>
              Number(i.status) === 1 &&
              !i.is_deleted &&
              String(i.ivr_category).toLowerCase() !== 'missed',
          )
          .map((i: any) => ({
            // The IVR's own id (not flow_id), like agent / call group / AI agent ids.
            id: i.id,
            name: i.flow_name,
            label: i.flow_name,
          }))
      : draftCallback.routing_action === 'agent'
      ? agentList
          .filter(
            (a: any) => String(a.access).toLowerCase() !== 'team_lead',
          )
          .map((a: any) => ({ id: a.id, name: a.agent_name, label: a.agent_name }))
      : draftCallback.routing_action === 'queue'
      ? groupList.map((g: any) => ({
          id: g.id,
          name: g.group_name,
          label: g.group_name,
        }))
      : [];

  const updateCallback = (updates: any) =>
    setDraftCallback((prev: any) => ({ ...prev, ...updates }));

  // All routing actions only when the backend NODE_ENV is "development"; anywhere else the
  // callback DID's inbound routing offers IVR only.
  const callbackRoutingActions = isDevelopmentEnv
    ? CALLBACK_ROUTING_ACTIONS
    : CALLBACK_ROUTING_ACTIONS.filter((o: any) => o.value === 'ivr');

  // Nodes saved before IVR targets used the IVR id hold its flow_id: switch them to the
  // id once the IVR list is in (the select routing and AI-agent transfer rules).
  useEffect(() => {
    if (!ivrList.length) return;
    const toIvrId = (value: any) => {
      if (value === '' || value == null) return value;
      if (ivrList.some((i: any) => String(i.id) === String(value))) return value;
      const legacy = ivrList.find((i: any) => String(i.flow_id) === String(value));
      return legacy ? String(legacy.id) : value;
    };
    setDraftCallback((prev: any) => {
      const next = { ...prev };
      let changed = false;
      if (prev.routing_action === 'ivr') {
        const id = toIvrId(prev.routing_target_id);
        if (id !== prev.routing_target_id) {
          next.routing_target_id = id;
          changed = true;
        }
      }
      if (Array.isArray(prev.routing_ai_rules)) {
        const rules = prev.routing_ai_rules.map((r: any) => {
          if (r?.transfer_type !== 'ivr') return r;
          const id = toIvrId(r.transfer_id);
          return id === r.transfer_id ? r : { ...r, transfer_id: id };
        });
        if (rules.some((r: any, idx: number) => r !== prev.routing_ai_rules[idx])) {
          next.routing_ai_rules = rules;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [ivrList]);

  const routingInfoLabel = (n: any) => {
    const type = n.routing_type ? String(n.routing_type) : 'Routed';
    return n.routing_destination ? `${type}: ${n.routing_destination}` : type;
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (data.userId) {
          const resp: any = await getApiCall(
            `${BACKEND_API_BASE_URL}/v2/prompt?user_id=${data.userId}`,
          );
          if (Array.isArray(resp)) {
            setPromptList(
              resp.filter(
                (p: any) =>
                  p.prompt_category === 'general' &&
                  Array.isArray(p.prompt_url) &&
                  p.prompt_url.some((e: any) => e.status === 1),
              ),
            );
          }
        }
        getApiCall(API_WEBHOOK_LIST)
          .then((r: any) => setWebhookList(r || []))
          .catch(console.log);
        getApiCall(API_SMSWEBHOOK_LIST)
          .then((r: any) => setSmsWebhookList(r || []))
          .catch(console.log);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [data.userId]);

  const updateNode = (updates: any) => updateNodeData(id, updates);

  const openModal = () => {
    const _answerCodes =
      getNodes().find((n: any) => n.type === 'answerNode')?.data?.languages ||
      [];
    const _langNode = findUpstreamLanguageNode(id, getNodes, getEdges);
    const _langCodes =
      _langNode &&
      Array.isArray(_langNode.data?.selectedLanguages) &&
      _langNode.data.selectedLanguages.length > 0
        ? _langNode.data.selectedLanguages
        : null;
    setHasLangParent(!!_langNode);
    setAnswerLangCodes(_langCodes ?? _answerCodes);
    setDraftMode(mode);
    setDraftAction(action);
    setDraftLanguage(action.language || '');
    setDraftCategory('general');
    setDraftDuration(maxDuration);
    setDraftWebhookId(action?.webhook_id || '');
    setDraftSmsWebhookId(action?.sms_webhook_id || '');
    setDraftCallback(callbackFromAction(action));
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const handleModeChange = (e: any) => {
    const m = e.target.value;
    setDraftMode(m);
    setDraftAction({});
    setDraftCategory('');
    setDraftDuration('');
    setDraftWebhookId('');
    setDraftSmsWebhookId('');
    setDraftCallback(emptyCallback());
  };

  const handleAudioChange = (e: any) => {
    const selected = promptList.find(
      (a: any) => a.id.toString() === e.target.value,
    );
    if (!selected) {
      setDraftAction((prev: any) => ({
        ...prev,
        id: '',
        audio: '',
        name: '',
        duration: 0,
      }));
      setDraftLanguage('');
      return;
    }
    const approvedLangs = Array.isArray(selected.prompt_url)
      ? selected.prompt_url.filter(
          (en: any) =>
            en.status === 1 &&
            (answerLangCodes.length === 0 ||
              answerLangCodes.includes(en.language)),
        )
      : [];

    if (hasLangParent) {
      // Downstream of a Language node — keep every approved language variant, no manual pick.
      setDraftAction({
        type: draftMode,
        prompt_category: 'general',
        id: selected.id,
        name: selected.prompt_name,
        duration: selected.prompt_duration || 0,
        languages: approvedLangs
          .filter((en: any) => en.status === 1)
          .map((en: any) => ({
            language: en.language,
            url: en.url,
            duration: en.duration ?? selected.prompt_duration ?? 0,
            status: en.status,
          })),
      });
      setDraftLanguage('');
      return;
    }

    const autoLang = approvedLangs.length === 1 ? approvedLangs[0].language : '';
    const autoAudio = approvedLangs.length === 1 ? approvedLangs[0].url : '';
    setDraftAction({
      type: draftMode,
      prompt_category: 'general',
      id: selected.id,
      audio: autoAudio,
      duration: selected.prompt_duration || 0,
      name: selected.prompt_name,
      language: autoLang,
    });
    setDraftLanguage(autoLang);
  };

  const handleLanguageChange = (e: any) => {
    const lang = e.target.value;
    setDraftLanguage(lang);
    const sel = promptList.find(
      (a: any) => a.id?.toString() === draftAction?.id?.toString(),
    );
    const entry =
      lang && sel && Array.isArray(sel.prompt_url)
        ? sel.prompt_url.find((en: any) => en.language === lang)
        : null;
    setDraftAction((prev: any) => ({
      ...prev,
      audio: entry?.url || '',
      language: lang,
    }));
  };

  const handleDurationChange = (e: any) => {
    const val = e.target.value;
    if (
      /^\d*$/.test(val) &&
      (val === '' || Number(val) <= VOICEMAIL_MAX_DURATION)
    ) {
      setDraftDuration(val);
    }
  };

  const handleWebhookChange = (e: any) => {
    const wid = e.target.value;
    setDraftWebhookId(wid);
    const selected = webhookList.find((w: any) => w.id.toString() === wid);
    setDraftAction({
      type: 'webhook',
      webhook_id: selected?.id || '',
      name: selected?.webhook_name || selected?.name || '',
      url: selected?.url || '',
    });
  };

  const handleSmsWebhookChange = (e: any) => {
    const wid = e.target.value;
    setDraftSmsWebhookId(wid);
    const selected = smsWebhookList.find((w: any) => w.id.toString() === wid);
    setDraftAction({
      type: 'sms_webhook',
      sms_webhook_id: selected?.id || '',
      name: selected?.webhook_name || '',
      url: selected?.url || '',
      payload: selected?.payload || '',
      request_type: selected?.request_type || '',
      sms_text: selected?.sms_text || '',
      header: selected?.header || '',
    });
  };

  const validate = () => {
    const newErrors: any = {};
    if (!draftMode) newErrors.mode = 'Mode is required';

    if (draftMode === 'hangup_with_media' || draftMode === 'voicemail') {
      if (draftCategory && !draftAction?.id) {
        newErrors.audio = 'Audio is required when category is selected';
      }
      if (!hasLangParent && draftAction?.id && !draftLanguage)
        newErrors.language = 'Please select a language';
    }
    if (draftMode === 'voicemail') {
      if (!draftDuration || Number(draftDuration) < 1)
        newErrors.duration = 'Duration required';
      else if (Number(draftDuration) > VOICEMAIL_MAX_DURATION)
        newErrors.duration = `Max ${VOICEMAIL_MAX_DURATION}s`;
    }
    if (draftMode === 'webhook' && !draftWebhookId)
      newErrors.webhook = 'Webhook required';
    if (draftMode === 'sms_webhook' && !draftSmsWebhookId)
      newErrors.smsWebhook = 'SMS Webhook required';
    if (draftMode === 'set_callback') {
      const cb = draftCallback;
      if (cb.callback_delay === '')
        newErrors.callbackDelay = 'Callback time required';
      if (cb.did_mode === CALLBACK_DID_MODES.OTHER) {
        if (!cb.did_category) newErrors.didCategory = 'Number type required';
        else if (!cb.did_id) newErrors.did = 'DID required';
      }
      if (callbackNeedsRouting(cb)) {
        if (!cb.routing_action)
          newErrors.routingAction = 'Routing action required';
        else if (cb.routing_target_id === '')
          newErrors.routingTarget =
            cb.routing_action === 'ai_agent'
              ? 'AI Agent required'
              : 'Routing destination required';
        else if (cb.routing_action === 'ai_agent') {
          const aiError = validateAiCompletionRules(cb.routing_ai_rules);
          if (aiError) newErrors.routingTarget = aiError;
        }
      }
      if (cb.retry_enabled) {
        if (cb.retry_count === '')
          newErrors.retryCount = 'Retry count required';
        if (cb.retry_interval === '')
          newErrors.retryInterval = 'Retry interval required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // `routing` mirrors the API_INBOUND_ROUTING body so the backend can apply it
  // when the IVR is saved. For the same DID `number` is null — the backend
  // resolves it to the DID this missed IVR is routed on.
  const buildCallbackAction = () => {
    const cb = draftCallback;
    const isOther = cb.did_mode === CALLBACK_DID_MODES.OTHER;
    const did = isOther
      ? callbackDids.find((n: any) => String(n.id) === String(cb.did_id))
      : null;
    const hasRouting = callbackNeedsRouting(cb);
    const isAiRouting = cb.routing_action === 'ai_agent';
    const target =
      hasRouting && !isAiRouting
        ? routingTargets.find(
            (t: any) => String(t.id) === String(cb.routing_target_id),
          )
        : null;
    return {
      type: 'set_callback',
      callback_delay: Number(cb.callback_delay),
      did_mode: cb.did_mode,
      did_category: isOther ? cb.did_category : null,
      did_id: isOther ? cb.did_id : null,
      did_number: isOther
        ? did?.actual_number ?? action?.did_number ?? null
        : null,
      did_routing:
        isOther && cb.did_category === CALLBACK_DID_CATEGORIES.ROUTED && did
          ? {
              // A DID routed to an IVR stores the IVR's flow_id; send the IVR's own id.
              routing_id: didRoutingTargetId(did),
              routing_type: did.routing_type ?? null,
              routing_destination: did.routing_destination ?? null,
            }
          : null,
      routing: hasRouting
        ? {
            number: isOther ? cb.did_id : null,
            numberType: 'DID',
            action: cb.routing_action,
            id: cb.routing_target_id,
            name: isAiRouting
              ? cb.routing_target_name
              : target?.name ?? action?.routing?.name ?? '',
            ...(cb.routing_action === 'agent' || cb.routing_action === 'queue'
              ? { agentSchedule: DEFAULT_TIME_SCHEDULE }
              : {}),
            ...(isAiRouting
              ? { completion_type: buildAiCompletionPayload(cb.routing_ai_rules) }
              : {}),
          }
        : null,
      retry_enabled: !!cb.retry_enabled,
      retry_count: cb.retry_enabled ? Number(cb.retry_count) : null,
      retry_interval: cb.retry_enabled ? Number(cb.retry_interval) : null,
    };
  };

  const handleSave = () => {
    if (!validate()) return;
    const updates: any = { mode: draftMode };
    if (draftMode === 'hangup') {
      updates.action = {};
      updates.max_duration = null;
    } else if (draftMode === 'webhook' || draftMode === 'sms_webhook') {
      updates.action = draftAction;
      updates.max_duration = null;
    } else if (draftMode === 'set_callback') {
      updates.action = buildCallbackAction();
      updates.max_duration = null;
    } else {
      updates.action = draftAction;
      updates.max_duration =
        draftMode === 'voicemail' ? Number(draftDuration) : null;
    }
    updateNode(updates);
    toast.success('Missed action updated.');
    setModalIsOpen(false);
  };

  const displayPromptList =
    answerLangCodes.length === 0
      ? promptList
      : promptList.filter(
          (p: any) =>
            Array.isArray(p.prompt_url) &&
            p.prompt_url.some(
              (e: any) =>
                e.status === 1 && answerLangCodes.includes(e.language),
            ),
        );

  const modeLabel =
    MISSED_NODE_OPTIONS.find((o: any) => o.value === mode)?.label || 'Missed';

  const renderListenBtn = (url: any, name: any) =>
    url ? (
      <button
        type="button"
        className="ivr-drawer-play-btn"
        title="Listen"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() =>
          setDrawerPreview({ open: true, url, name: name || 'Audio' })
        }
        style={{ marginLeft: 8 }}
      >
        ▶
      </button>
    ) : null;

  return (
    <div className="ivrn" style={{ '--node-color': '#9333ea' } as any}>
      <span
        className="ivrn-icon"
        style={{ background: '#9333ea', color: '#fff' }}
        onClick={triggerHandleConnect}
      >
        <PhoneMissed size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={'left' as any}
        id="missed-target"
        style={{ background: '#9333ea', top: '65%' }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Missed Call</span>
      </div>
      <div className="ivrn-body">
        {mode ? (
          <span className="ivrn-val">{modeLabel}</span>
        ) : (
          <span className="ivrn-hint">Click ⚙ to set action</span>
        )}
      </div>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Missed Call Action"
        subtitle="Configure the action taken when a call is missed."
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Close
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </>
        }
      >
        <Form>
          <Form.Group>
            <Form.Label>
              Action<Form.Text className="text-danger">*</Form.Text>
            </Form.Label>
            <Form.Control
              as="select"
              value={draftMode}
              onChange={handleModeChange}
            >
              <option value="">Select Action</option>
              {MISSED_NODE_OPTIONS.map((m: any) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Form.Control>
            {errors.mode && (
              <Form.Text className="text-danger">{errors.mode}</Form.Text>
            )}
          </Form.Group>

          {(draftMode === 'hangup_with_media' ||
            draftMode === 'voicemail') && (
            <>
              {draftMode === 'hangup_with_media' && (
                <Form.Text className="text-warning">
                  Note: Ring Duration will be limited to 2 seconds.
                </Form.Text>
              )}
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>Select Audio</Form.Label>
                <Form.Control
                  as="select"
                  value={draftAction?.id || ''}
                  onChange={handleAudioChange}
                >
                  <option value="">Select Audio</option>
                  {displayPromptList.map((item: any) => (
                    <option key={item.id} value={item.id}>
                      {item.prompt_name}
                    </option>
                  ))}
                </Form.Control>
                {errors.audio && (
                  <Form.Text className="text-danger">{errors.audio}</Form.Text>
                )}
                {renderListenBtn(
                  draftAction?.audio ||
                    draftAction?.languages?.[0]?.url ||
                    '',
                  draftAction?.name,
                )}
              </Form.Group>
              {hasLangParent ? (
                Array.isArray(draftAction?.languages) &&
                draftAction.languages.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      fontSize: 12,
                      color: '#0f766e',
                      background: '#f0fdfa',
                      border: '1px solid #99f6e4',
                      borderRadius: 8,
                      padding: '8px 12px',
                    }}
                  >
                    This node is downstream of a Language node — all{' '}
                    {draftAction.languages.length} approved language
                    {draftAction.languages.length !== 1 ? 's' : ''} (
                    {draftAction.languages
                      .map((l: any) => l.language?.toUpperCase())
                      .join(', ')}
                    ) will be saved automatically.
                  </div>
                )
              ) : (
                (() => {
                  const sel = promptList.find(
                    (a: any) =>
                      a.id?.toString() === draftAction?.id?.toString(),
                  );
                  const langs = Array.isArray(sel?.prompt_url)
                    ? sel.prompt_url.filter(
                        (e: any) =>
                          e.status === 1 &&
                          (answerLangCodes.length === 0 ||
                            answerLangCodes.includes(e.language)),
                      )
                    : [];
                  if (!sel || langs.length === 0) return null;
                  return (
                    <Form.Group style={{ marginTop: 10 }}>
                      <Form.Label>
                        Select Audio Language{' '}
                        <span style={{ color: 'red' }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={draftLanguage}
                        onChange={handleLanguageChange}
                      >
                        <option value="">Select Language</option>
                        {langs.map((e: any) => (
                          <option key={e.language} value={e.language}>
                            {e.language?.toUpperCase()}
                            {e.name ? ` — ${e.name}` : ''}
                          </option>
                        ))}
                      </Form.Control>
                      {errors.language && (
                        <Form.Text className="text-danger">
                          {errors.language}
                        </Form.Text>
                      )}
                    </Form.Group>
                  );
                })()
              )}
            </>
          )}

          {draftMode === 'voicemail' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Max Voicemail Duration (sec, max {VOICEMAIL_MAX_DURATION})
                <Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                type="number"
                min="1"
                max={VOICEMAIL_MAX_DURATION}
                value={draftDuration}
                onChange={handleDurationChange}
              />
              {errors.duration && (
                <Form.Text className="text-danger">
                  {errors.duration}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {draftMode === 'webhook' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Select Webhook<Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                as="select"
                value={draftWebhookId}
                onChange={handleWebhookChange}
              >
                <option value="">Select Webhook</option>
                {webhookList.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.webhook_name || w.name}
                  </option>
                ))}
              </Form.Control>
              {errors.webhook && (
                <Form.Text className="text-danger">{errors.webhook}</Form.Text>
              )}
            </Form.Group>
          )}

          {draftMode === 'sms_webhook' && (
            <Form.Group style={{ marginTop: 15 }}>
              <Form.Label>
                Select SMS Webhook
                <Form.Text className="text-danger">*</Form.Text>
              </Form.Label>
              <Form.Control
                as="select"
                value={draftSmsWebhookId}
                onChange={handleSmsWebhookChange}
              >
                <option value="">Select SMS Webhook</option>
                {smsWebhookList.map((w: any) => (
                  <option key={w.id} value={w.id}>
                    {w.webhook_name}
                  </option>
                ))}
              </Form.Control>
              {errors.smsWebhook && (
                <Form.Text className="text-danger">
                  {errors.smsWebhook}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {draftMode === 'set_callback' && (
            <>
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>
                  Callback After
                  <Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={draftCallback.callback_delay}
                  onChange={(e: any) =>
                    updateCallback({ callback_delay: e.target.value })
                  }
                >
                  <option value="">Select Time</option>
                  {CALLBACK_INTERVAL_OPTIONS.map((o: any) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Control>
                <Form.Text className="text-muted d-block">
                  Time after the missed call when the callback is initiated.
                </Form.Text>
                {errors.callbackDelay && (
                  <Form.Text className="text-danger">
                    {errors.callbackDelay}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>
                  Callback DID<Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <div style={{ display: 'flex', gap: 24 }}>
                  <Form.Check
                    type="radio"
                    id={`cb-did-same-${id}`}
                    name={`cb-did-mode-${id}`}
                    label="Same DID (missed call number)"
                    checked={draftCallback.did_mode === CALLBACK_DID_MODES.SAME}
                    onChange={() =>
                      updateCallback({
                        did_mode: CALLBACK_DID_MODES.SAME,
                        did_category: '',
                        did_id: '',
                        routing_action: '',
                        routing_target_id: '',
                      })
                    }
                  />
                  <Form.Check
                    type="radio"
                    id={`cb-did-other-${id}`}
                    name={`cb-did-mode-${id}`}
                    label="Other DID"
                    checked={draftCallback.did_mode === CALLBACK_DID_MODES.OTHER}
                    onChange={() =>
                      updateCallback({ did_mode: CALLBACK_DID_MODES.OTHER })
                    }
                  />
                </div>
              </Form.Group>

              {draftCallback.did_mode === CALLBACK_DID_MODES.OTHER && (
                <>
                  <Form.Group style={{ marginTop: 15 }}>
                    <Form.Label>
                      Number Type
                      <Form.Text className="text-danger">*</Form.Text>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={draftCallback.did_category}
                      onChange={(e: any) =>
                        updateCallback({
                          did_category: e.target.value,
                          did_id: '',
                          routing_action: '',
                          routing_target_id: '',
                        })
                      }
                    >
                      <option value="">Select Number Type</option>
                      <option value={CALLBACK_DID_CATEGORIES.FREE}>
                        Free Numbers
                      </option>
                      <option value={CALLBACK_DID_CATEGORIES.ROUTED}>
                        Already Routed Numbers
                      </option>
                    </Form.Control>
                    {errors.didCategory && (
                      <Form.Text className="text-danger">
                        {errors.didCategory}
                      </Form.Text>
                    )}
                  </Form.Group>

                  {draftCallback.did_category && (
                    <Form.Group style={{ marginTop: 15 }}>
                      <Form.Label>
                        Select DID
                        <Form.Text className="text-danger">*</Form.Text>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={draftCallback.did_id}
                        onChange={(e: any) =>
                          updateCallback({ did_id: e.target.value })
                        }
                      >
                        <option value="">
                          {categoryDids.length === 0
                            ? 'No numbers available'
                            : 'Select DID'}
                        </option>
                        {categoryDids.map((n: any) => (
                          <option key={n.id} value={n.id}>
                            {draftCallback.did_category ===
                            CALLBACK_DID_CATEGORIES.ROUTED
                              ? `${n.actual_number} — ${routingInfoLabel(n)}`
                              : n.actual_number}
                          </option>
                        ))}
                      </Form.Control>
                      {errors.did && (
                        <Form.Text className="text-danger">{errors.did}</Form.Text>
                      )}
                    </Form.Group>
                  )}
                </>
              )}

              {callbackNeedsRouting(draftCallback) && (
                      <div
                        style={{
                          marginTop: 15,
                          padding: 12,
                          border: '1px solid #dee2e6',
                          borderRadius: 8,
                        }}
                      >
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Inbound Routing for this DID
                        </div>
                        <Form.Text className="text-muted d-block">
                          {draftCallback.did_mode === CALLBACK_DID_MODES.SAME
                            ? 'Choose where calls to the missed call number should go.'
                            : 'This number has no routing yet. Choose where calls to it should go.'}
                        </Form.Text>
                        <Form.Group style={{ marginTop: 10 }}>
                          <Form.Label>
                            Action
                            <Form.Text className="text-danger">*</Form.Text>
                          </Form.Label>
                          <Form.Control
                            as="select"
                            value={draftCallback.routing_action}
                            onChange={(e: any) =>
                              updateCallback({
                                routing_action: e.target.value,
                                routing_target_id: '',
                                routing_target_name: '',
                                routing_ai_rules: [],
                              })
                            }
                          >
                            <option value="">Select Action</option>
                            {callbackRoutingActions.map((o: any) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Form.Control>
                          {errors.routingAction && (
                            <Form.Text className="text-danger">
                              {errors.routingAction}
                            </Form.Text>
                          )}
                        </Form.Group>
                        {draftCallback.routing_action === 'ai_agent' && (
                          <div style={{ marginTop: 10 }}>
                            <AiAgentCompletionConfig
                              selectedAiAgentId={draftCallback.routing_target_id}
                              onSelectAiAgent={(agentId: string, name: string) =>
                                updateCallback({
                                  routing_target_id: agentId,
                                  routing_target_name: name,
                                })
                              }
                              rules={draftCallback.routing_ai_rules}
                              onRulesChange={(rules: any[]) =>
                                updateCallback({ routing_ai_rules: rules })
                              }
                              agents={agentList}
                              callGroups={groupList}
                              ivrs={ivrList}
                              ivrIdKey="id"
                            />
                            {errors.routingTarget && (
                              <Form.Text className="text-danger">
                                {errors.routingTarget}
                              </Form.Text>
                            )}
                          </div>
                        )}
                        {draftCallback.routing_action &&
                          draftCallback.routing_action !== 'ai_agent' && (
                          <Form.Group style={{ marginTop: 10, marginBottom: 0 }}>
                            <Form.Label>
                              Select{' '}
                              {CALLBACK_ROUTING_ACTIONS.find(
                                (o: any) =>
                                  o.value === draftCallback.routing_action,
                              )?.label}
                              <Form.Text className="text-danger">*</Form.Text>
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={draftCallback.routing_target_id}
                              onChange={(e: any) =>
                                updateCallback({
                                  routing_target_id: e.target.value,
                                })
                              }
                            >
                              <option value="">Select</option>
                              {routingTargets.map((t: any) => (
                                <option
                                  key={t.id}
                                  value={t.id}
                                  disabled={t.disabled}
                                >
                                  {t.label}
                                </option>
                              ))}
                            </Form.Control>
                            {errors.routingTarget && (
                              <Form.Text className="text-danger">
                                {errors.routingTarget}
                              </Form.Text>
                            )}
                          </Form.Group>
                        )}
                      </div>
                    )}

              <Form.Group style={{ marginTop: 15 }}>
                <Form.Check
                  type="checkbox"
                  id={`cb-retry-enabled-${id}`}
                  label="Enable Retry"
                  checked={!!draftCallback.retry_enabled}
                  onChange={(e: any) =>
                    updateCallback({
                      retry_enabled: e.target.checked,
                      retry_count: '',
                      retry_interval: '',
                    })
                  }
                />
              </Form.Group>

              {draftCallback.retry_enabled && (
              <>
              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>
                  Retry Count<Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={draftCallback.retry_count}
                  onChange={(e: any) =>
                    updateCallback({ retry_count: e.target.value })
                  }
                >
                  <option value="">Select Retry Count</option>
                  {CALLBACK_RETRY_COUNT_OPTIONS.map((n: any) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Form.Control>
                {errors.retryCount && (
                  <Form.Text className="text-danger">
                    {errors.retryCount}
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group style={{ marginTop: 15 }}>
                <Form.Label>
                  Retry Interval
                  <Form.Text className="text-danger">*</Form.Text>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={draftCallback.retry_interval}
                  onChange={(e: any) =>
                    updateCallback({ retry_interval: e.target.value })
                  }
                >
                  <option value="">Select Interval</option>
                  {CALLBACK_INTERVAL_OPTIONS.map((o: any) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Control>
                {errors.retryInterval && (
                  <Form.Text className="text-danger">
                    {errors.retryInterval}
                  </Form.Text>
                )}
              </Form.Group>
              </>
              )}
            </>
          )}
        </Form>
      </IvrDrawer>

      <AudioPreviewModal
        show={drawerPreview.open}
        onClose={() =>
          setDrawerPreview({ open: false, url: '', name: '' })
        }
        audioUrl={drawerPreview.url}
        title={drawerPreview.name}
      />
    </div>
  );
};

export default MissedNode;
